import type { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import type {
  ProposalContentOperation,
  ProposalNegotiationParty,
  ProposalResolutionState,
  ProposalReviewerChange,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import { proposalCollectionItemHasIdentity } from "work/proposals/negotiation/proposal-operation-identity";
import { proposalResolutionDecisionTargetsState } from "work/proposals/negotiation/proposal-operation-containment";

const unsafePathSegments = new Set(["__proto__", "constructor", "prototype"]);

/**
 * The top-level fields the server masks per reader. Each one's schema requires `isMasked`, and the
 * server sends some of them as `null` until they hold a value: a vendor team until one is chosen.
 */
const maskableProposalFields: ReadonlySet<string> = new Set([
  "clientTeam",
  "vendorTeam",
  "teamRestricted",
  "feeSchedule",
  "discount",
  "clientPolicies",
  "vendorPolicies",
  "conflictsCheckWaived",
  "conflicts",
]);

/**
 * Materializes the user's local operation queue over the latest authorized
 * server projection. The server remains authoritative for conflict detection;
 * this function only makes refresh/realtime delivery deterministic in-browser.
 */
export function projectProposalOperations(
  projection: ProposalJSON,
  operations: ProposalContentOperation[],
): ProposalJSON {
  const result = structuredClone(projection) as unknown as Record<string, unknown>;
  const appliedOperationIds = new Set<string>();

  for (const operation of operations) {
    if (appliedOperationIds.has(operation.operationId)) continue;
    if (operation.kind === "ResolutionDecision") {
      appliedOperationIds.add(operation.operationId);
      continue;
    }
    applyProposalOperation(result, operation);
    appliedOperationIds.add(operation.operationId);
  }

  return result as ProposalJSON;
}

/**
 * Applies local/in-flight decisions to the authoritative envelope state without
 * inventing server actor or audit metadata. A decision against a superseded
 * incoming value is deliberately ignored.
 */
export function projectProposalResolutionStates(
  states: readonly ProposalResolutionState[],
  operations: readonly ProposalContentOperation[],
): ProposalResolutionState[] {
  const result = states.map((state) => structuredClone(state));
  const appliedOperationIds = new Set<string>();

  for (const operation of operations) {
    if (operation.kind !== "ResolutionDecision" || appliedOperationIds.has(operation.operationId)) continue;
    const state = result.find((candidate) => proposalResolutionDecisionTargetsState(operation, candidate));
    if (state) state.disposition = operation.value;
    appliedOperationIds.add(operation.operationId);
  }

  return result;
}

export function readProposalOperationValue(projection: ProposalJSON, path: string): unknown {
  return readAtPath(projection as unknown as Record<string, unknown>, parsePointer(path));
}

export function projectProposalReviewerChanges(
  projection: ProposalJSON,
  party: ProposalNegotiationParty,
  reviewerChanges: ProposalReviewerChange[],
): ProposalJSON {
  const result = structuredClone(projection);
  const reviewers = party === "Client" ? result.clientReviewers : result.vendorReviewers;

  for (const change of reviewerChanges) {
    const reviewerIndex = reviewers.findIndex(
      (reviewer) =>
        reviewer.userId === change.reviewerUserId && reviewer.legalEntityId === change.reviewerLegalEntityId,
    );

    if (change.kind === "Assign") {
      if (reviewerIndex === -1) {
        reviewers.push({
          userId: change.reviewerUserId,
          legalEntityId: change.reviewerLegalEntityId,
          isEditing: change.canEdit,
          customMessage: change.customMessage ?? null,
        });
      }
    } else if (change.kind === "Remove") {
      if (reviewerIndex !== -1) reviewers.splice(reviewerIndex, 1);
    } else if (change.kind === "SetCanEdit" && reviewerIndex !== -1) {
      reviewers[reviewerIndex] = { ...reviewers[reviewerIndex], isEditing: change.canEdit };
    } else if (change.kind === "SetCustomMessage" && reviewerIndex !== -1) {
      reviewers[reviewerIndex] = { ...reviewers[reviewerIndex], customMessage: change.customMessage };
    }
  }

  return result;
}

function applyProposalOperation(
  target: Record<string, unknown>,
  operation: Exclude<ProposalContentOperation, { kind: "ResolutionDecision" }>,
): void {
  const path = parsePointer(operation.path);

  if (operation.kind === "FieldSet") {
    writeAtPath(target, path, operation.value);
    return;
  }

  // A collection's parent can legitimately be absent — a vendor team does not exist until
  // one is chosen — so the first add materializes the collection and its parents instead
  // of failing. Updates and removals still require the collection to be there already.
  const collection =
    operation.kind === "CollectionItemAdded"
      ? ensureCollectionAtPath(target, path)
      : readAtPath(target, path);
  if (!Array.isArray(collection)) {
    return;
  }

  const existingIndex = collection.findIndex((item) => proposalCollectionItemHasIdentity(item, operation.itemId));
  switch (operation.kind) {
    case "CollectionItemAdded":
      if (existingIndex === -1) collection.push(structuredClone(operation.value));
      return;
    case "CollectionItemUpdated":
      if (existingIndex === -1) return;
      collection[existingIndex] = mergeCollectionValue(collection[existingIndex], operation.value);
      return;
    case "CollectionItemRemoved":
      if (existingIndex !== -1) collection.splice(existingIndex, 1);
      return;
  }
}

/**
 * Un-escapes one JSON-pointer segment (RFC 6901: `~1` → `/`, `~0` → `~`). The one place this decode is
 * implemented; other pointer readers in this codebase (`proposal-resolution-redline-adapter.ts`,
 * `proposal-resolution-decision-builder.ts`, `proposal-collaboration-local-store.ts`) call this instead
 * of re-implementing the same two `replace`s, each of which needs its own surrounding
 * validation/error-handling (this one throws on an unsafe or empty segment; the others return a
 * "not found" sentinel for a malformed pointer instead), so only the decode step — not the differing
 * traversal/error behavior around it — is shared.
 */
export function decodeProposalPointerSegment(segment: string): string {
  return segment.replace(/~1/g, "/").replace(/~0/g, "~");
}

function parsePointer(pointer: string): string[] {
  if (!pointer.startsWith("/") || pointer === "/") {
    throw new Error(`Invalid proposal operation path: ${pointer}`);
  }

  return pointer
    .slice(1)
    .split("/")
    .map(decodeProposalPointerSegment)
    .map((segment) => {
      if (!segment || unsafePathSegments.has(segment)) {
        throw new Error(`Unsafe proposal operation path segment: ${segment || "<empty>"}`);
      }
      return segment;
    });
}

function readAtPath(target: unknown, path: string[]): unknown {
  let current = target;
  for (const segment of path) {
    if (!isRecord(current)) return undefined;
    current = current[segment];
  }
  return current;
}

/**
 * Walks to the value's owner, materializing any absent object along the way.
 *
 * A maskable field it materializes says it is not masked. The reader is the one writing to it, and the
 * proposal schema refuses the field without the flag (#7071).
 */
function resolveParentAtPath(target: Record<string, unknown>, path: string[]): Record<string, unknown> | undefined {
  let parent = target;
  for (const [depth, segment] of path.slice(0, -1).entries()) {
    const existing = parent[segment];
    if (existing === undefined || existing === null) {
      parent[segment] = depth === 0 && maskableProposalFields.has(segment) ? { isMasked: false } : {};
    } else if (!isRecord(existing)) {
      return undefined;
    }
    parent = parent[segment] as Record<string, unknown>;
  }
  return parent;
}

function writeAtPath(target: Record<string, unknown>, path: string[], value: unknown): void {
  const parent = resolveParentAtPath(target, path);
  if (!parent) return;
  const property = path.at(-1)!;
  if (value === undefined) delete parent[property];
  else parent[property] = structuredClone(value);
}

function ensureCollectionAtPath(target: Record<string, unknown>, path: string[]): unknown {
  const parent = resolveParentAtPath(target, path);
  if (!parent) return undefined;
  const property = path.at(-1)!;
  const existing = parent[property];
  if (existing === undefined || existing === null) parent[property] = [];
  return parent[property];
}

function mergeCollectionValue(existing: unknown, update: unknown): unknown {
  if (!isRecord(existing) || !isRecord(update)) return structuredClone(update);
  const merged = structuredClone(existing);
  for (const [key, value] of Object.entries(update)) {
    merged[key] = isRecord(merged[key]) && isRecord(value) ? mergeCollectionValue(merged[key], value) : structuredClone(value);
  }
  return merged;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
